import { Box, Button, Typography } from '@mui/material'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import BacktestReportCard from './backtests/BacktestReportCard'
import { useApplicationSuiteContext } from '../contexts/ApplicationSuiteProvider'
import AssessmentIcon from '@mui/icons-material/Assessment'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { PatternTreeProvider } from '../app/builder/contexts/PatternTreeContext'
import { Pattern, PatternProvider } from '../app/builder/contexts/PatternContext'
import PatternListItem from '../app/builder/components/PatternListItem'
import { AlertProvider } from '../app/alerts/contexts/AlertContext'
import AlertCard from '../app/alerts/components/AlertCard'
import { BookmarkAdd, NotificationAdd } from '@mui/icons-material'
import { collection, doc, setDoc } from 'firebase/firestore'
import { firebase_firestore } from '../common/firebaseConfig'
import { SnackbarHelperContext } from '../contexts/SnackbarHelperContext'
import { useSavePattern } from '../hooks/useSavePattern'
import { Tabs } from '../layout/MainLayout'
import MultiSlideDialog, { SlideStep } from './common/MultiSlideDialog'
import { copyPatternToAccount } from '../common/utils/copyPatternToAccount'

function ShareableDialog({
  shareableData,
  setShareableData,
}: {
  shareableData: any
  setShareableData: (data: any) => void
}) {
  const { setActiveTab } = useApplicationSuiteContext()

  const [open, setOpen] = useState(false)
  // The copy saved into the user's account, so "Take me there" opens it rather than the original
  const [savedPatternCopy, setSavedPatternCopy] = useState<Pattern | null>(null)

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const { usermeta, canCreateAlerts, tradingHouse, canViewPrivatePatterns } =
    useApplicationSuiteContext()

  const { forceSave } = useSavePattern(
    () => {}, // We don't need to update the pattern state here
    usermeta,
    tradingHouse,
    canViewPrivatePatterns
  )

  // Update open state when shareableData changes
  useMemo(() => {
    setOpen(shareableData !== null)
  }, [shareableData])

  const onClose = useCallback(() => {
    setOpen(false)
    setSavedPatternCopy(null)
    setShareableData(null)
  }, [setShareableData])

  const sharedItemName: any = {
    PATTERN: 'Pattern',
    BACKTEST: 'Backtest',
    ALERT: 'Alert',
  }

  const addPatternToAccount = useCallback(async () => {
    if (!shareableData.data) return

    try {
      setSnackbarSettings({
        message: 'Copying pattern to your account...',
        severity: 'info',
        autoHideDuration: 3000,
      })

      const savedPattern = await copyPatternToAccount(
        shareableData.data,
        forceSave,
        shareableData.shareable.createdBy?.gcid
      )

      if (savedPattern) {
        setSavedPatternCopy(savedPattern)
        setSnackbarSettings({
          message: 'Pattern copied successfully!',
          severity: 'success',
          autoHideDuration: 3000,
        })
      }
    } catch (error) {
      console.error('Error copying pattern:', error)
      setSnackbarSettings({
        message: 'Error copying pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
  }, [shareableData.data, shareableData.shareable, forceSave, setSnackbarSettings])

  const viewBacktest = useCallback(async () => {
    setActiveTab({ tab: Tabs.Showcase, options: { item: shareableData.data } })
    onClose()
  }, [setActiveTab, shareableData.data, onClose])

  const addBacktestToFavorites = useCallback(async () => {
    const savedShowcaseRef = collection(
      firebase_firestore,
      `usermeta/${usermeta.gcid}/savedShowcase`
    )
    try {
      await setDoc(doc(savedShowcaseRef, shareableData.data.id), shareableData.data)
      setSnackbarSettings({
        message: 'Backtest added to your favorites!',
        severity: 'success',
        autoHideDuration: 3000,
      })
    } catch (error) {
      console.error('Error adding document: ', error)
    }
  }, [usermeta, shareableData.data, setSnackbarSettings])

  const addAlertToAccount = useCallback(async () => {
    if (!canCreateAlerts) {
      return
    }

    const docRef = doc(collection(firebase_firestore, 'tradingPlans'))
    const alertDataToSave = structuredClone(shareableData.data)
    const originalGcid = shareableData.shareable.createdBy?.gcid

    // Extract exit pattern if present (won't be saved on entry alert)
    let exitTradingPlanPattern = alertDataToSave.exitTradingPlanPattern
    delete alertDataToSave.exitTradingPlanPattern

    // Copy entry pattern to user's account
    if (alertDataToSave.pattern) {
      const savedEntryPattern = await copyPatternToAccount(
        alertDataToSave.pattern,
        forceSave,
        originalGcid
      )
      if (savedEntryPattern) {
        alertDataToSave.pattern = savedEntryPattern
      }
    }

    // If shared alert had an exit pattern, recreate exit trading plan for this user
    let exitTradingPlanId = undefined
    if (exitTradingPlanPattern) {
      // Copy exit pattern to user's account
      const savedExitPattern = await copyPatternToAccount(
        exitTradingPlanPattern,
        forceSave,
        originalGcid
      )
      exitTradingPlanPattern = savedExitPattern || exitTradingPlanPattern

      const exitDocRef = doc(collection(firebase_firestore, 'tradingPlans'))
      exitTradingPlanId = exitDocRef.id

      await setDoc(exitDocRef, {
        id: exitDocRef.id,
        name: `${alertDataToSave.name} - Exit`,
        pattern: exitTradingPlanPattern,
        symbol:
          alertDataToSave.symbol || alertDataToSave.pattern?.trigger?.chartDetails?.symbol || '',
        user: {
          gcid: usermeta.gcid,
          email: usermeta.email,
          ...(usermeta?.alertPhoneNumber?.verified && {
            phoneNumber: usermeta.alertPhoneNumber.phoneNumber,
          }),
        },
        active: false,
        timezone: alertDataToSave.timezone,
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        timeRanges: [{ startTimeRange: '00:00', endTimeRange: '00:00' }],
        alertTypes: ['exitTradingPlan'],
      })
    }

    // Save the entry alert
    await setDoc(docRef, {
      ...alertDataToSave,
      id: docRef.id,
      active: false,
      user: {
        gcid: usermeta.gcid,
        email: usermeta.email,
        ...(usermeta?.alertPhoneNumber?.verified && {
          phoneNumber: usermeta.alertPhoneNumber.phoneNumber,
        }),
      },
      // If we created an exit plan, link it (user still needs to connect trading account)
      ...(exitTradingPlanId &&
        alertDataToSave.trading && {
          trading: {
            ...alertDataToSave.trading,
            exitTradingPlanId,
          },
        }),
    })
  }, [canCreateAlerts, usermeta, shareableData.data, shareableData.shareable, forceSave])

  const takeMeThere = useCallback(() => {
    if (shareableData.shareable.type === 'PATTERN') {
      setActiveTab({
        tab: Tabs.PatternBuilder,
        options: { item: savedPatternCopy || shareableData.data },
      })
    } else if (shareableData.shareable.type === 'BACKTEST') {
      setActiveTab({ tab: Tabs.Showcase, options: { item: shareableData.data, type: 'favorites' } })
    } else if (shareableData.shareable.type === 'ALERT') {
      setActiveTab({ tab: Tabs.Alerts, options: { item: shareableData.data } })
    }
    onClose()
  }, [shareableData.data, savedPatternCopy, onClose, setActiveTab, shareableData.shareable.type])

  const steps: SlideStep[] = useMemo(
    () =>
      getSteps(shareableData, {
        addAlertToAccount,
        addPatternToAccount,
        viewBacktest,
        addBacktestToFavorites,
        takeMeThere,
      }),
    [
      shareableData,
      addAlertToAccount,
      addPatternToAccount,
      viewBacktest,
      addBacktestToFavorites,
      takeMeThere,
    ]
  )

  if (!shareableData) {
    return null
  }

  return (
    <MultiSlideDialog
      open={open}
      onClose={onClose}
      title={`Shared ${sharedItemName[shareableData.shareable.type]}`}
      steps={steps}
      slideProps={{ shareableData }}
      actionProps={{ shareableData }}
      showProgressBar={false}
    />
  )
}

const shareableText: {
  [key: string]: { title: string; description: string; icon: React.ReactNode }
} = {
  PATTERN: {
    title: 'You Received a Pattern',
    description:
      "Patterns contain groups of rules that are used to identify trade entries. You can backtest patterns to see historical performance or create alerts to get real-time notifications when your pattern's rules are met.",
    icon: <AccountTreeIcon sx={{ fontSize: '64px' }} color={'success'} />,
  },
  BACKTEST: {
    title: 'You Received a Backtest',
    description:
      'Backtests give you insights into how your trading strategy would have performed in the past. You can view the results in your backtests section.',
    icon: <AssessmentIcon sx={{ fontSize: '64px' }} color={'success'} />,
  },
  ALERT: {
    title: 'You Received an Alert',
    description: "Alerts are used to get instant notifications when a pattern's rules are met.",
    icon: <NotificationsActiveIcon sx={{ fontSize: '64px' }} color={'success'} />,
  },
}

function ShareStartComponent({ shareableData }: any) {
  const shareableType = shareableData.shareable.type

  const icon = shareableText[shareableType].icon
  const title = shareableText[shareableType].title
  const description = shareableText[shareableType].description

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      gap={2}
      px={2}
      alignItems={'center'}
      textAlign={'center'}
    >
      {icon}
      <Typography variant={'h4'} fontWeight={'bold'}>
        {title}
      </Typography>
      <Typography variant={'body2'} color={'textSecondary'}>
        {description}
      </Typography>
    </Box>
  )
}

function ShareViewComponent({ shareableData }: any) {
  if (shareableData.shareable.type === 'BACKTEST') {
    return <BacktestReportCard backtest={shareableData.data} />
  } else if (shareableData.shareable.type === 'PATTERN') {
    return (
      <PatternProvider>
        <AlertProvider>
          <PatternTreeProvider pattern={shareableData.data as Pattern}>
            <PatternListItem defaultOpen disableRowExpand />
          </PatternTreeProvider>
        </AlertProvider>
      </PatternProvider>
    )
  } else if (shareableData.shareable.type === 'ALERT') {
    return (
      <AlertProvider>
        <AlertCard alert={shareableData.data} readOnly />
      </AlertProvider>
    )
  }
}

function getSteps(
  shareableData: any,
  { addAlertToAccount, addPatternToAccount, viewBacktest, addBacktestToFavorites, takeMeThere }: any
): SlideStep[] {
  const shareableViewActionsComponent: any = {
    PATTERN: ({ onNextStep }: any) => (
      <Button
        onClick={async () => {
          await addPatternToAccount()
          onNextStep()
        }}
        variant="contained"
      >
        Add to Your Patterns
      </Button>
    ),
    BACKTEST: ({ onNextStep }: any) => (
      <Box display="flex" gap={1}>
        <Button
          onClick={async () => {
            await addBacktestToFavorites()
            onNextStep()
          }}
          variant="contained"
          endIcon={<BookmarkAdd />}
        >
          Add to Your Favorites
        </Button>
      </Box>
    ),
    ALERT: ({ onNextStep }: any) => (
      <Button
        onClick={async () => {
          await addAlertToAccount()
          onNextStep()
        }}
        variant="contained"
        endIcon={<NotificationAdd />}
      >
        Add to Your Alerts
      </Button>
    ),
  }

  const shareableAddedConfirmationComponent: any = {
    ALERT: () => (
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        px={2}
        alignItems="center"
        textAlign="center"
      >
        <Typography variant="h6">Alert added to your account!</Typography>
        <Typography variant="body2" color="textSecondary">
          Alerts are used to get instant notifications when a pattern's rules are met.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can view your newly added alert in your alerts section.
        </Typography>
      </Box>
    ),
    PATTERN: () => (
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        px={2}
        alignItems="center"
        textAlign="center"
      >
        <Typography variant="h6">Pattern added to your account!</Typography>
        <Typography variant="body2" color="textSecondary">
          Patterns contain groups of rules that are used to identify trade entries. You can backtest
          patterns to see historical performance or create alerts to get real-time notifications
          when the pattern's rules are met.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can view your newly added pattern in your patterns section.
        </Typography>
      </Box>
    ),
    BACKTEST: () => (
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        px={2}
        alignItems="center"
        textAlign="center"
      >
        <Typography variant="h6">Backtest added to your favorites!</Typography>
        <Typography variant="body2" color="textSecondary">
          Backtests give you insights into how your trading strategy would have performed in the
          past.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can view your newly favorited backtest in your favorites section.
        </Typography>
      </Box>
    ),
  }

  const steps: SlideStep[] = [
    {
      slideComponent: ShareStartComponent,
      actionsComponent: ({ onNextStep }: any) => (
        <Button onClick={onNextStep} variant="contained">
          View
        </Button>
      ),
    },
    {
      slideComponent: ShareViewComponent,
      actionsComponent: shareableViewActionsComponent[shareableData.shareable.type],
    },
    {
      slideComponent: shareableAddedConfirmationComponent[shareableData.shareable.type],
      actionsComponent: () => (
        <Button onClick={takeMeThere} variant="contained">
          Take me there
        </Button>
      ),
    },
  ]

  return steps
}

export default ShareableDialog
